import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classnames from "classnames";
import { compareAsc, differenceInMilliseconds, addMinutes } from "date-fns";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import ReactTooltip from "react-tooltip";

import { LabOrder, Stain } from "pwapi";

import selectStainByLabStainId from "shared/selectors/selectStainByLabStainId";
import { splitFlagReason } from "shared/utils/labOrderUtils";

import ConversationModal from "conversations/ConversationModal";

import LinkButton from "components/LinkButton";
import { useAppSelector } from "store/hooks";

type ActiveStainOrderProps = {
  order: LabOrder;
  cancelOrder: Function;
  hideStatusIcons?: boolean;
};

const ActiveStainOrder = ({
  order,
  cancelOrder,
  hideStatusIcons,
}: ActiveStainOrderProps) => {
  const [isCancelable, setIsCancelable] = useState<boolean>(false);
  const [conversationOpen, setConversationOpen] = useState<boolean>(false);
  const conversationEnabled = useSelector(
    (state: any) => !!state?.sys?.features?.conversation
  );
  const slides = useAppSelector(({ caseDetail }) => caseDetail.slides);
  const slide = slides.find((s) => s.id === order.slide_id);
  const block = useAppSelector(({ caseDetail }) =>
    caseDetail.blocks?.find((b) => b.id === slide?.block_obj_id)
  );
  const specimen = useAppSelector(({ caseDetail }) =>
    caseDetail.specimens?.find((s) => s.id === block?.specimen_id)
  );

  const flags = slides.map((s) => s.flags).flat();

  const flag = flags?.find((f) => f.order_id === order.id);

  const orderStain = useAppSelector(
    selectStainByLabStainId(order?.lab_stain_id || 0)
  );
  const slideStain = useAppSelector(
    ({ stains }) => stains[slide?.stain_id || 0]
  );

  useEffect(() => {
    let timer: any = undefined;
    // TODO: Skip when order.sent_at is defined. Need to expose this in the API first.
    // This is low priority because it only happens with seeded data.
    if (order.created_at && !order.completed_at) {
      const cancelDate = addMinutes(new Date(order.created_at), 5);
      if (compareAsc(new Date(), cancelDate) === -1) {
        setIsCancelable(true);
        timer = setTimeout(() => {
          setIsCancelable(false);
        }, differenceInMilliseconds(cancelDate, new Date()));
      }
    }

    return () => {
      clearTimeout(timer);
    };
  }, []);

  return (
    <div className="stainDetails__container">
      <div className="stainDetails__container__order">
        {order.type === "rescan" ? (
          <div>
            Rescan{" "}
            <strong>
              {specimen?.name || ""}
              {block?.name || ""}S{slide?.slide_number || 1}{" "}
              {slideStain?.abbreviation}
            </strong>
          </div>
        ) : (
          <div>
            <strong>{orderStain?.abbreviation}</strong>
            {orderStain?.category !== Stain.category.ROUTINE && (
              <span className="stainDetails__container__category">
                {" "}
                {orderStain?.category}
              </span>
            )}
          </div>
        )}

        <div className="stainDetails__created_at">
          {order.created_at &&
            `(${new Date(order.created_at).toLocaleString()})`}
        </div>

        {isCancelable && (
          <LinkButton onClick={() => cancelOrder(order)}>Cancel</LinkButton>
        )}

        {!hideStatusIcons && !isCancelable && (
          <>
            <ReactTooltip id={`order-${order.id}-status`}>
              {order.completed_at
                ? `Order Completed ${new Date(
                    order.completed_at
                  ).toLocaleString()}`
                : " Order Sent"}
            </ReactTooltip>
            <FontAwesomeIcon
              data-tip=""
              data-for={`order-${order.id}-status`}
              icon={order.completed_at ? "check-circle" : "play-circle"}
              height="1em"
              className={classnames("ActiveStainOrder__status", {
                completed: order.completed_at,
                started: !order.completed_at,
              })}
            />
          </>
        )}
      </div>

      {flag ? (
        <div>
          <FontAwesomeIcon icon="flag" />
          {splitFlagReason(flag.reason)} {flag.note && " - "}
          <span className="stainDetails__container__note">
            {flag.note}{" "}
            <span className="stainDetails__container__user">
              {flag.user?.first_name} {flag.user?.last_name}
            </span>
          </span>
        </div>
      ) : order.note || order.user ? (
        <div>
          <span className="stainDetails__container__note">
            {order.note}{" "}
            <span className="stainDetails__container__user">
              {order.user?.first_name} {order.user?.last_name}
            </span>
          </span>
        </div>
      ) : null}

      {conversationEnabled && (
        <LinkButton onClick={() => setConversationOpen(true)}>
          Discuss
        </LinkButton>
      )}

      {conversationEnabled && order.id && (
        <ConversationModal
          isOpen={conversationOpen}
          topicType="laborder"
          topicId={order.id}
          closeModal={() => setConversationOpen(false)}
        />
      )}
    </div>
  );
};

export default ActiveStainOrder;
